'use client'

import {
  ChangeEvent,
  FocusEvent,
  MouseEvent,
  ComponentProps,
  useCallback,
  useEffect,
  useState,
  RefObject,
  ReactNode,
} from 'react'

import {
  DEFAULT_DATE,
  formatDate,
  isNilOrEmptyString,
  parseDate,
  cn,
  Slot,
  usePopover,
} from '@/shared/lib'

import { Calendar, type Props as CalendarProps } from '../calendar'
import { Icon } from '../icon'
import { Input } from '../input'
import * as Popover from '../popover'

const INPUT_TYPE_CONTENT_OPTIONS = {
  align: 'start',
  alignOffset: -4,
  sideOffset: 10,
} as const

const SELECT_TYPE_CONTENT_OPTIONS = {
  align: 'center',
} as const

type DatePickerTriggerProps = Omit<
  ComponentProps<typeof Input>,
  'value' | 'ref'
> & {
  value?: string
  onTriggerClick?: (event: MouseEvent<HTMLButtonElement>) => void
  onTriggerFocus?: (event: FocusEvent<HTMLButtonElement>) => void
  onTriggerBlur?: (event: FocusEvent<HTMLButtonElement>) => void
  ref?: RefObject<HTMLDivElement | null>
}

const SelectTrigger = ({
  ref,
  fullWidth,
  className,
  onTriggerClick,
  onTriggerFocus,
  onTriggerBlur,
  children,
  ...props
}: DatePickerTriggerProps) => {
  return (
    <Popover.Trigger
      asChild
      onClick={onTriggerClick}
      onFocus={onTriggerFocus}
      onBlur={onTriggerBlur}
    >
      <div className={cn(fullWidth && 'w-full', className)}>
        <Input
          {...props}
          fullWidth={fullWidth}
          wrapperRef={ref}
          type="text"
          tabIndex={-1}
          className={cn(
            'cursor-pointer border-none bg-[rgba(0,0,0,0)] px-[5px] py-0.5 text-[15px]! leading-[22px]!',
            '*:placeholder:text-gray-400 *:read-only:cursor-pointer',
            className,
          )}
        >
          <Slot name="left">
            <Icon
              name="calendar"
              className="text-black"
              size="24"
              strokeWidth={1.5}
            />
          </Slot>
          {children}
        </Input>
      </div>
    </Popover.Trigger>
  )
}

const InputTrigger = ({
  ref,
  className,
  onTriggerClick,
  onTriggerFocus,
  onTriggerBlur,
  children,
  readOnly,
  disabled,
  ...restProps
}: DatePickerTriggerProps) => {
  return (
    <Input
      {...restProps}
      wrapperRef={ref}
      type="text"
      readOnly={readOnly}
      disabled={disabled}
      className={cn(className)}
      tabIndex={-1}
    >
      <Slot name="left">
        <Popover.Trigger
          asChild
          onClick={onTriggerClick}
          onFocus={onTriggerFocus}
          onBlur={onTriggerBlur}
        >
          <button
            className={cn(
              'relative -mx-2 cursor-pointer rounded-full p-2 transition-all',
              !(readOnly || disabled) && 'hover:bg-black/5',
              !(readOnly || disabled) && 'focus-visible:bg-black/5',
            )}
            aria-label="날짜 선택"
            tabIndex={0}
          >
            <Icon
              name="calendar"
              className="text-black"
              size="24"
              strokeWidth={1.5}
            />
          </button>
        </Popover.Trigger>
      </Slot>
      {children}
    </Input>
  )
}

export type SingleDatePickerProps = Omit<CalendarProps, 'mode'> &
  Pick<
    ComponentProps<typeof Popover.Content>,
    'align' | 'alignOffset' | 'sideOffset'
  > &
  Omit<
    ComponentProps<typeof Input>,
    'value' | 'onChange' | 'onFocus' | 'onBlur' | 'onClick'
  > & {
    value?: string
    onChange?: (date?: string) => void
    type?: 'input' | 'select'
    popoverClassName?: string
    openOnFocus?: boolean
    onClick?: (event: MouseEvent<HTMLButtonElement>) => void
    onFocus?: (event: FocusEvent<HTMLButtonElement>) => void
    onBlur?: (event: FocusEvent<HTMLButtonElement>) => void
    render?: (props: {
      selected: Date | undefined
      onSelect: (date?: Date) => void
      onClose: () => void
    }) => ReactNode
  }

export const SingleDatePicker = ({
  type = 'select',
  value,
  onChange,
  readOnly: readOnlyProp,
  'aria-readonly': ariaReadOnly,
  disabled,
  openOnFocus = false,
  align,
  alignOffset,
  sideOffset,
  onClick,
  onFocus,
  onBlur,
  children,
  render,
  popoverClassName,
  ...props
}: SingleDatePickerProps) => {
  const { isOpen, ref, open, close, toggleOpen, handleOutsideClick } =
    usePopover<HTMLDivElement>({
      disabled: disabled || readOnlyProp || Boolean(ariaReadOnly),
    })

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    DEFAULT_DATE,
  )

  const [inputValue, setInputValue] = useState('')

  const isInputReadOnly = (() => {
    if (type === 'select') {
      return true
    }

    return readOnlyProp || Boolean(ariaReadOnly)
  })()

  const updateDate = useCallback((date?: Date | string) => {
    if (isNilOrEmptyString(date)) {
      setInputValue('')
      setSelectedDate(DEFAULT_DATE)

      return
    }

    if (typeof date === 'string') {
      setInputValue(formatDate({ date }))
      setSelectedDate(parseDate({ value: date }))

      return
    }

    setInputValue(formatDate({ date }))
    setSelectedDate(date)
  }, [])

  const handleDayPickerSelect = (date?: Date) => {
    updateDate(date)
    onChange?.(formatDate({ date }))

    close()
  }

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value)

    const parsedDate = parseDate({ value: event.target.value })

    setSelectedDate(parsedDate)
    onChange?.(formatDate({ date: parsedDate }))
  }

  const handleTriggerClick = (event: MouseEvent<HTMLButtonElement>) => {
    toggleOpen()
    onClick?.(event)
  }

  const handleTriggerFocus = (event: FocusEvent<HTMLButtonElement>) => {
    if (openOnFocus) {
      open()
    }

    onFocus?.(event)
  }

  const handleTriggerBlur = (event: FocusEvent<HTMLButtonElement>) => {
    if (selectedDate) {
      setInputValue(formatDate({ date: selectedDate }))
    }

    onBlur?.(event)
  }

  useEffect(() => {
    if (value === formatDate({ date: selectedDate })) return

    updateDate(value)
  }, [updateDate, value])

  return (
    <Popover.Root open={isOpen}>
      {type === 'input' ? (
        <InputTrigger
          {...props}
          ref={ref}
          value={inputValue}
          onChange={handleInputChange}
          readOnly={isInputReadOnly}
          disabled={disabled}
          onTriggerFocus={handleTriggerFocus}
          onTriggerBlur={handleTriggerBlur}
          onTriggerClick={handleTriggerClick}
        >
          {children}
        </InputTrigger>
      ) : (
        <SelectTrigger
          {...props}
          ref={ref}
          value={inputValue}
          onChange={handleInputChange}
          readOnly={isInputReadOnly}
          disabled={disabled}
          onTriggerFocus={handleTriggerFocus}
          onTriggerBlur={handleTriggerBlur}
          onTriggerClick={handleTriggerClick}
        >
          {children}
        </SelectTrigger>
      )}
      <Popover.Content
        closeButton={false}
        arrow={false}
        onInteractOutside={handleOutsideClick}
        onEscapeKeyDown={close}
        className={popoverClassName}
        {...(type === 'input'
          ? INPUT_TYPE_CONTENT_OPTIONS
          : SELECT_TYPE_CONTENT_OPTIONS)}
        {...(align && { align })}
        {...(alignOffset && { alignOffset })}
        {...(sideOffset && { sideOffset })}
      >
        {(() => {
          if (render) {
            return render({
              selected: selectedDate,
              onSelect: handleDayPickerSelect,
              onClose: close,
            })
          }

          return (
            <Calendar
              mode="single"
              required={false}
              className="p-2 shadow-none"
              selected={selectedDate}
              onSelect={handleDayPickerSelect}
            />
          )
        })()}
      </Popover.Content>
    </Popover.Root>
  )
}
